import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Container,
    Grid,
    Paper,
    Typography,
    Box,
    Card,
    CardContent,
    Chip,
    Button,
    IconButton,
    Tooltip,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Divider,
    Avatar,
    Fade,
    Zoom,
    ToggleButton,
    ToggleButtonGroup,
    Snackbar,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    AttachMoney,
    People,
    Receipt,
    Download,
    Refresh,
    Assessment,
    CheckCircle,
    Cancel,
    Pending,
    CurrencyExchange,
    WifiOff,
    CloudDone,
    Inventory,
    TrendingUp,
    TrendingDown,
    AccountBalance,
} from '@mui/icons-material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    AreaChart,
    Area,
} from 'recharts';
import { format, subMonths } from 'date-fns';
import * as XLSX from "xlsx";
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff6b6b', '#4ecdc4', '#45b7d1'];
const STATUS_COLORS = {
    'Pending': '#FF9800',
    'Yes': '#4CAF50',
    'No': '#F44336'
};

const STATUS_ICONS = {
    'Pending': <Pending />,
    'Yes': <CheckCircle />,
    'No': <Cancel />
};

const CURRENCY_SYMBOLS = {
    'USD': '$',
    'KES': 'KSh',
    'EUR': '€',
    'GBP': '£',
    'CNY': '¥',
    'JPY': '¥',
    'AUD': 'A$',
    'CAD': 'C$',
    'CHF': 'Fr',
    'NZD': 'NZ$',
    'ZAR': 'R',
    'INR': '₹',
    'NGN': '₦',
    'UGX': 'USh',
    'TZS': 'TSh',
    'RWF': 'FRw',
};

export default function Analytics() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [clients, setClients] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [selectedCurrency, setSelectedCurrency] = useState('USD');
    const [availableCurrencies, setAvailableCurrencies] = useState(['USD', 'KES']);
    const [exchangeRates, setExchangeRates] = useState({});
    const [ratesLoading, setRatesLoading] = useState(false);
    const [ratesError, setRatesError] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const token = localStorage.getItem("token");

    const fetchLiveExchangeRates = async (baseCurrency = 'USD') => {
        setRatesLoading(true);
        setRatesError(false);
        try {
            const response = await axios.get(
                `https://api.exchangerate-api.com/v4/latest/${baseCurrency}`
            );
            const rates = response.data.rates;
            setExchangeRates(rates);
            setRatesError(false);

            let displayRate = 'N/A';
            if (baseCurrency === 'USD' && rates.KES) {
                displayRate = `${rates.KES.toFixed(4)} KES`;
            } else if (baseCurrency === 'KES' && rates.USD) {
                displayRate = `${rates.USD.toFixed(4)} USD`;
            } else {
                const otherCurrency = Object.keys(rates).find(c => c !== baseCurrency);
                if (otherCurrency) {
                    displayRate = `${rates[otherCurrency].toFixed(4)} ${otherCurrency}`;
                }
            }

            setSnackbar({
                open: true,
                message: `Exchange rates updated (1 ${baseCurrency} = ${displayRate})`,
                severity: 'success'
            });
            return rates;
        } catch (error) {
            console.error("Error fetching exchange rates from primary API:", error);
            try {
                const fallbackResponse = await axios.get(
                    `https://api.frankfurter.app/latest?from=${baseCurrency}`
                );
                const rates = fallbackResponse.data.rates;
                setExchangeRates(rates);
                setRatesError(false);

                let displayRate = 'N/A';
                if (baseCurrency === 'USD' && rates.KES) {
                    displayRate = `${rates.KES.toFixed(4)} KES`;
                } else if (baseCurrency === 'KES' && rates.USD) {
                    displayRate = `${rates.USD.toFixed(4)} USD`;
                } else {
                    const otherCurrency = Object.keys(rates).find(c => c !== baseCurrency);
                    if (otherCurrency) {
                        displayRate = `${rates[otherCurrency].toFixed(4)} ${otherCurrency}`;
                    }
                }

                setSnackbar({
                    open: true,
                    message: `Exchange rates updated (1 ${baseCurrency} = ${displayRate})`,
                    severity: 'success'
                });
                return rates;
            } catch (fallbackError) {
                console.error("Fallback API also failed:", fallbackError);
                setRatesError(true);
                setSnackbar({
                    open: true,
                    message: 'Failed to fetch live exchange rates. Using cached rates.',
                    severity: 'warning'
                });
                if (Object.keys(exchangeRates).length === 0) {
                    setExchangeRates(getDefaultRates());
                }
                return exchangeRates;
            }
        } finally {
            setRatesLoading(false);
        }
    };

    const getDefaultRates = () => {
        return {
            'USD': 1,
            'KES': 150.50,
            'EUR': 0.85,
            'GBP': 0.73,
            'CNY': 6.45,
            'JPY': 110.00,
            'AUD': 1.35,
            'CAD': 1.25,
            'CHF': 0.92,
            'NZD': 1.42,
            'ZAR': 15.00,
            'INR': 75.00,
            'NGN': 410.00,
            'UGX': 3700.00,
            'TZS': 2300.00,
            'RWF': 1000.00,
        };
    };

    const getLiveRate = (fromCurrency, toCurrency) => {
        if (fromCurrency === toCurrency) return 1;

        if (exchangeRates && Object.keys(exchangeRates).length > 0) {
            const fromRate = exchangeRates[fromCurrency];
            const toRate = exchangeRates[toCurrency];

            if (fromRate && toRate) {
                if (fromRate === 1) {
                    return toRate;
                }
                return toRate / fromRate;
            }
        }

        const defaultRates = getDefaultRates();
        const fromDefault = defaultRates[fromCurrency];
        const toDefault = defaultRates[toCurrency];

        if (fromDefault && toDefault) {
            return toDefault / fromDefault;
        }

        return 1;
    };

    const convertAmount = (amount, fromCurrency, toCurrency) => {
        if (!amount) return 0;
        const numAmount = parseFloat(amount);
        if (isNaN(numAmount)) return 0;

        if (fromCurrency === toCurrency) {
            return numAmount;
        }

        // Use live rate for conversion
        const liveRate = getLiveRate(fromCurrency, toCurrency);
        if (liveRate && liveRate !== 1) {
            return numAmount * liveRate;
        }

        return numAmount;
    };

    useEffect(() => {
        fetchData();
        fetchLiveExchangeRates('USD');
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch clients/transactions
            const clientsRes = await axios.get(`${API_URL}/clients`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('Clients data:', clientsRes.data);
            setClients(clientsRes.data);

            // Fetch expenses
            const expensesRes = await axios.get(`${API_URL}/api/expenses/user/1/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('Expenses data:', expensesRes.data.data.expenses);
            setExpenses(expensesRes.data.data.expenses);

            const currencies = [...new Set(clientsRes.data.map(c => c.currency).filter(Boolean))];
            setAvailableCurrencies(['USD', 'KES', ...currencies.filter(c => c !== 'USD' && c !== 'KES')]);

            // Process analytics after both data sets are loaded
            processAnalytics(clientsRes.data, expensesRes.data.data.expenses, selectedCurrency);
        } catch (err) {
            console.error('Error fetching data:', err);
            toast.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const processAnalytics = (data, expenseData, targetCurrency) => {
        console.log('Processing analytics with:', {
            clientsCount: data.length,
            expensesCount: expenseData?.length || 0,
            targetCurrency
        });

        // STEP 1: Create a map of expenses by client_transaction_id
        const expensesByTransaction = {};
        if (expenseData && expenseData.length > 0) {
            expenseData.forEach(expense => {
                if (expense.client_transaction_id) {
                    if (!expensesByTransaction[expense.client_transaction_id]) {
                        expensesByTransaction[expense.client_transaction_id] = [];
                    }
                    expensesByTransaction[expense.client_transaction_id].push(expense);
                }
            });
        }
        console.log('Expenses by transaction:', Object.keys(expensesByTransaction).length);

        // STEP 2: Convert transactions and calculate profit
        const processedData = data.map(transaction => {
            const storedRate = transaction.exchange_rate || null;
            const fromCurrency = transaction.currency || 'USD';
            const baseCurrency = transaction.base_currency || null;

            let services = transaction.nature_of_service;
            if (typeof services === 'string') {
                try {
                    services = JSON.parse(services);
                } catch (e) {
                    services = [services];
                }
            }
            if (!Array.isArray(services)) {
                services = [services];
            }

            // Convert transaction amount to target currency
            const convertedAmount = convertAmount(
                transaction.quotation_amount,
                fromCurrency,
                targetCurrency
            );

            // Calculate total expenses linked to this transaction
            const transactionExpenses = expensesByTransaction[transaction.service_id] || [];
            let totalExpenses = 0;
            let totalExpensesOriginal = 0;
            transactionExpenses.forEach(expense => {
                // Get the expense currency (default to KES if not specified)
                const expenseCurrency = expense.currency || 'KES';
                const expenseAmount = parseFloat(expense.amount) || 0;
                totalExpensesOriginal += expenseAmount;

                // Convert each expense to target currency
                const convertedExpense = convertAmount(
                    expenseAmount,
                    expenseCurrency,
                    targetCurrency
                );
                totalExpenses += convertedExpense;
            });

            // Calculate profit
            const profit = convertedAmount - totalExpenses;
            const profitMargin = convertedAmount > 0 ? (profit / convertedAmount) * 100 : 0;

            return {
                ...transaction,
                converted_amount: convertedAmount,
                original_currency: fromCurrency,
                target_currency: targetCurrency,
                exchange_rate_used: storedRate,
                base_currency: baseCurrency,
                parsed_services: services.filter(s => s && s.trim()),
                package_name: transaction.package_name || '',
                total_expenses: totalExpenses,
                total_expenses_original: totalExpensesOriginal,
                profit: profit,
                profit_margin: profitMargin,
                expenses_count: transactionExpenses.length,
                expenses: transactionExpenses,
            };
        });

        console.log('Processed transactions:', processedData.length);

        // STEP 3: Group by client
        const groupedClients = {};
        processedData.forEach(transaction => {
            const key = transaction.customer_id || transaction.contact_name || 'unknown';

            if (!groupedClients[key]) {
                groupedClients[key] = {
                    customer_id: transaction.customer_id,
                    contact_name: transaction.contact_name,
                    company_name: transaction.company_name,
                    email: transaction.email,
                    phone_number: transaction.phone_number,
                    nationality: transaction.nationality,
                    transactions: [],
                    total_revenue: 0,
                    total_expenses: 0,
                    total_profit: 0,
                    total_original_amount: 0,
                    currency: transaction.currency || 'KES',
                    base_currency: transaction.base_currency || 'USD',
                };
            }

            groupedClients[key].transactions.push(transaction);
            groupedClients[key].total_original_amount += parseFloat(transaction.quotation_amount) || 0;
            groupedClients[key].total_revenue += transaction.converted_amount || 0;
            groupedClients[key].total_expenses += transaction.total_expenses || 0;
            groupedClients[key].total_profit += transaction.profit || 0;
        });

        const groupedClientsArray = Object.values(groupedClients);
        console.log('Grouped clients:', groupedClientsArray.length);

        // STEP 4: Calculate totals
        const totalClients = groupedClientsArray.length;
        const totalRevenue = groupedClientsArray.reduce((sum, c) => sum + (c.total_revenue || 0), 0);
        const totalExpenses = groupedClientsArray.reduce((sum, c) => sum + (c.total_expenses || 0), 0);
        const totalProfit = groupedClientsArray.reduce((sum, c) => sum + (c.total_profit || 0), 0);
        const averageRevenue = totalClients > 0 ? totalRevenue / totalClients : 0;
        const averageProfit = totalClients > 0 ? totalProfit / totalClients : 0;
        const overallProfitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

        // STEP 5: Top clients by profit
        const topProfitableClients = [...groupedClientsArray]
            .sort((a, b) => (b.total_profit || 0) - (a.total_profit || 0))
            .slice(0, 10);

        // STEP 6: Status breakdown
        const statusBreakdown = {};
        processedData.forEach(transaction => {
            const status = transaction.conversion_status || 'Pending';
            statusBreakdown[status] = (statusBreakdown[status] || 0) + 1;
        });

        // STEP 7: Service count
        const serviceCount = {};
        processedData.forEach(transaction => {
            const serviceName = transaction.package_name ||
                (transaction.parsed_services && transaction.parsed_services.length > 0
                    ? transaction.parsed_services[0]
                    : 'Unknown');

            if (serviceName && serviceName.trim()) {
                serviceCount[serviceName] = (serviceCount[serviceName] || 0) + 1;
            }
        });

        // STEP 8: Monthly trend
        const monthlyTrend = {};
        processedData.forEach(transaction => {
            const month = format(new Date(transaction.booking_date), 'MMM yyyy');
            const amount = transaction.converted_amount || 0;
            const profit = transaction.profit || 0;
            if (!monthlyTrend[month]) {
                monthlyTrend[month] = { month, count: 0, revenue: 0, profit: 0 };
            }
            monthlyTrend[month].count += 1;
            monthlyTrend[month].revenue += amount;
            monthlyTrend[month].profit += profit;
        });

        // STEP 9: Service revenue
        const serviceRevenue = {};
        processedData.forEach(transaction => {
            const serviceName = transaction.package_name ||
                (transaction.parsed_services && transaction.parsed_services.length > 0
                    ? transaction.parsed_services[0]
                    : 'Unknown');
            const amount = transaction.converted_amount || 0;
            if (serviceName && serviceName.trim()) {
                serviceRevenue[serviceName] = (serviceRevenue[serviceName] || 0) + amount;
            }
        });

        // STEP 10: Package analytics
        const packageAnalytics = {};
        processedData.forEach(transaction => {
            if (transaction.package_name) {
                const amount = transaction.converted_amount || 0;
                if (!packageAnalytics[transaction.package_name]) {
                    packageAnalytics[transaction.package_name] = {
                        count: 0,
                        total_amount: 0,
                        transactions: []
                    };
                }
                packageAnalytics[transaction.package_name].count += 1;
                packageAnalytics[transaction.package_name].total_amount += amount;
                packageAnalytics[transaction.package_name].transactions.push(transaction);
            }
        });

        // STEP 11: Currency breakdown
        const currencyBreakdown = {};
        processedData.forEach(transaction => {
            const currency = transaction.original_currency || 'USD';
            const amount = transaction.converted_amount || 0;
            if (!currencyBreakdown[currency]) {
                currencyBreakdown[currency] = {
                    total: 0,
                    count: 0,
                    converted_total: 0,
                };
            }
            currencyBreakdown[currency].total += parseFloat(transaction.quotation_amount) || 0;
            currencyBreakdown[currency].converted_total += amount;
            currencyBreakdown[currency].count += 1;
        });

        const analyticsData = {
            processedData,
            groupedClients: groupedClientsArray,
            totalClients,
            totalRevenue,
            totalExpenses,
            totalProfit,
            averageRevenue,
            averageProfit,
            overallProfitMargin,
            statusBreakdown,
            serviceCount,
            monthlyTrend: Object.values(monthlyTrend).sort((a, b) => {
                const dateA = new Date(a.month);
                const dateB = new Date(b.month);
                return dateA - dateB;
            }),
            serviceRevenue: Object.entries(serviceRevenue).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value),
            topProfitableClients,
            currencyBreakdown,
            targetCurrency,
            ratesUpdated: new Date(),
            totalTransactions: processedData.length,
            packageAnalytics: Object.entries(packageAnalytics).map(([name, data]) => ({
                name,
                count: data.count,
                total_amount: data.total_amount,
                transactions: data.transactions
            })).sort((a, b) => b.total_amount - a.total_amount),
        };

        console.log('Analytics data:', analyticsData);
        setAnalytics(analyticsData);
    };

    useEffect(() => {
        if (clients.length > 0 && expenses.length > 0 && !analytics) {
            processAnalytics(clients, expenses, selectedCurrency);
        }
    }, [clients, expenses, selectedCurrency]);

    const handleCurrencyChange = (event, newCurrency) => {
        if (newCurrency !== null) {
            setSelectedCurrency(newCurrency);
            fetchLiveExchangeRates(newCurrency);
            // Re-process analytics with new currency
            if (clients.length > 0 && expenses.length > 0) {
                processAnalytics(clients, expenses, newCurrency);
            }
            toast.success(`Switched to ${newCurrency} view`);
        }
    };

    const handleRefreshRates = () => {
        fetchLiveExchangeRates(selectedCurrency);
    };

    const formatCurrency = (amount, currency = null) => {
        if (!amount && amount !== 0) return '0';
        const symbol = currency ? CURRENCY_SYMBOLS[currency] || currency : CURRENCY_SYMBOLS[selectedCurrency] || selectedCurrency;
        return `${symbol} ${parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return format(new Date(date), 'dd MMM yyyy');
    };

    const exportAnalytics = () => {
        if (!analytics) return;

        const exportData = analytics.groupedClients.map(c => ({
            "Contact Name": c.contact_name,
            "Company Name": c.company_name,
            Email: c.email,
            Phone: c.phone_number,
            "Nationality": c.nationality,
            "Original Currency": c.currency,
            "Number of Transactions": c.transactions.length,
            "Total Revenue": c.total_revenue?.toFixed(2) || '0',
            "Total Expenses": c.total_expenses?.toFixed(2) || '0',
            "Total Profit": c.total_profit?.toFixed(2) || '0',
            "Profit Margin (%)": c.total_revenue > 0 ? ((c.total_profit / c.total_revenue) * 100).toFixed(2) : '0',
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Profit Analysis");
        XLSX.writeFile(wb, `profit_analytics_${selectedCurrency}_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`);
        toast.success('Analytics exported successfully');
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!analytics || analytics.totalClients === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" flexDirection="column" gap={2}>
                <Typography variant="h6" color="text.secondary">
                    No data available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Clients: {clients.length} • Expenses: {expenses.length}
                </Typography>
                <Button variant="contained" onClick={fetchData}>
                    Refresh Data
                </Button>
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Header */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                mb: 3,
                gap: 2
            }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        Profit Analytics Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {analytics.totalClients} clients • {analytics.totalTransactions} transactions • {analytics.processedData?.reduce((sum, t) => sum + (t.expenses_count || 0), 0) || 0} linked expenses
                        {analytics.ratesUpdated && (
                            <span> • Rates updated: {format(new Date(analytics.ratesUpdated), 'dd MMM yyyy HH:mm')}</span>
                        )}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Tooltip title="Refresh Exchange Rates">
                        <Button
                            variant="outlined"
                            startIcon={ratesLoading ? <CircularProgress size={20} /> : <Refresh />}
                            onClick={handleRefreshRates}
                            size={isMobile ? 'small' : 'medium'}
                            disabled={ratesLoading}
                            sx={{ borderColor: ratesError ? '#f44336' : '#1976d2' }}
                        >
                            {ratesLoading ? 'Loading...' : 'Refresh Rates'}
                        </Button>
                    </Tooltip>
                    <Tooltip title="Refresh Data">
                        <IconButton
                            onClick={fetchData}
                            size={isMobile ? 'small' : 'medium'}
                            sx={{ color: '#1976d2' }}
                        >
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Analytics">
                        <Button
                            variant="contained"
                            startIcon={<Download />}
                            onClick={exportAnalytics}
                            size={isMobile ? 'small' : 'medium'}
                            sx={{
                                background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
                                '&:hover': {
                                    background: 'linear-gradient(45deg, #45a049 30%, #3d8b40 90%)',
                                },
                                color: 'white',
                            }}
                        >
                            Export
                        </Button>
                    </Tooltip>
                </Box>
            </Box>

            {/* Currency Selector */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                        View all data in:
                    </Typography>
                    <ToggleButtonGroup
                        value={selectedCurrency}
                        exclusive
                        onChange={handleCurrencyChange}
                        size="medium"
                        sx={{
                            flexWrap: 'wrap',
                            '& .MuiToggleButton-root': {
                                px: 3,
                                py: 1.5,
                                borderColor: '#e0e0e0',
                                fontWeight: 'bold',
                                '&.Mui-selected': {
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: '#1565c0',
                                    }
                                }
                            }
                        }}
                    >
                        {availableCurrencies.map((currency) => (
                            <ToggleButton key={currency} value={currency}>
                                <CurrencyExchange sx={{ mr: 1 }} />
                                {currency} {CURRENCY_SYMBOLS[currency] ? `(${CURRENCY_SYMBOLS[currency]})` : ''}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    {ratesError ? (
                        <Chip
                            icon={<WifiOff />}
                            label="Using cached rates"
                            color="warning"
                            size="small"
                            variant="outlined"
                        />
                    ) : (
                        <Chip
                            icon={<CloudDone />}
                            label="Live rates"
                            color="success"
                            size="small"
                            variant="outlined"
                        />
                    )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Each transaction and expense converted individually to {selectedCurrency}, then profit calculated
                </Typography>
            </Box>

            {/* KPI Cards */}
            <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={6} md={3}>
                    <Zoom in={true}>
                        <Card sx={{
                            height: '100%',
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                        }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                            Total Clients
                                        </Typography>
                                        <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold' }}>
                                            {analytics.totalClients}
                                        </Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                                        <People />
                                    </Avatar>
                                </Box>
                            </CardContent>
                        </Card>
                    </Zoom>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <Zoom in={true}>
                        <Card sx={{
                            height: '100%',
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            color: 'white',
                        }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                            Total Revenue
                                        </Typography>
                                        <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 'bold', fontSize: isMobile ? '1rem' : '2rem' }}>
                                            {formatCurrency(analytics.totalRevenue)}
                                        </Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                                        <AttachMoney />
                                    </Avatar>
                                </Box>
                            </CardContent>
                        </Card>
                    </Zoom>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <Zoom in={true}>
                        <Card sx={{
                            height: '100%',
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            color: 'white',
                        }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                            Total Expenses
                                        </Typography>
                                        <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 'bold', fontSize: isMobile ? '1rem' : '2rem' }}>
                                            {formatCurrency(analytics.totalExpenses)}
                                        </Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                                        <AccountBalance />
                                    </Avatar>
                                </Box>
                            </CardContent>
                        </Card>
                    </Zoom>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <Zoom in={true}>
                        <Card sx={{
                            height: '100%',
                            borderRadius: 2,
                            background: analytics.totalProfit >= 0
                                ? 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
                                : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            color: 'white',
                        }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                            Total Profit
                                        </Typography>
                                        <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 'bold', fontSize: isMobile ? '1rem' : '2rem' }}>
                                            {formatCurrency(analytics.totalProfit)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                            Margin: {analytics.overallProfitMargin.toFixed(1)}%
                                        </Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                                        {analytics.totalProfit >= 0 ? <TrendingUp /> : <TrendingDown />}
                                    </Avatar>
                                </Box>
                            </CardContent>
                        </Card>
                    </Zoom>
                </Grid>
            </Grid>

            {/* Top Profitable Clients Table */}
            <Fade in={true}>
                <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            Top 10 Most Profitable Clients (in {selectedCurrency})
                        </Typography>
                        <Chip
                            label={`Total Profit: ${formatCurrency(analytics.totalProfit)}`}
                            sx={{
                                backgroundColor: analytics.totalProfit >= 0 ? '#4CAF50' : '#F44336',
                                color: 'white',
                                '& .MuiChip-label': { fontWeight: 'bold' }
                            }}
                            size="small"
                        />
                    </Box>
                    <TableContainer>
                        <Table size={isMobile ? 'small' : 'medium'}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Client</TableCell>
                                    <TableCell align="right">Revenue</TableCell>
                                    <TableCell align="right">Expenses</TableCell>
                                    <TableCell align="right">Profit</TableCell>
                                    <TableCell align="right">Margin</TableCell>
                                    <TableCell align="center">Transactions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {analytics.topProfitableClients.map((client, index) => {
                                    const profitMargin = client.total_revenue > 0
                                        ? (client.total_profit / client.total_revenue) * 100
                                        : 0;
                                    return (
                                        <TableRow key={client.customer_id || index} hover>
                                            <TableCell>
                                                <Chip
                                                    label={`#${index + 1}`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: COLORS[index % COLORS.length],
                                                        color: 'white',
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="500">
                                                        {client.contact_name || 'Unknown'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {client.company_name || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" color="primary" fontWeight="bold">
                                                    {formatCurrency(client.total_revenue)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" color="error">
                                                    {formatCurrency(client.total_expenses)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="bold"
                                                    sx={{ color: client.total_profit >= 0 ? '#4CAF50' : '#F44336' }}
                                                >
                                                    {formatCurrency(client.total_profit)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Chip
                                                    label={`${profitMargin.toFixed(1)}%`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: profitMargin >= 20 ? '#4CAF50' :
                                                            profitMargin >= 0 ? '#FF9800' : '#F44336',
                                                        color: 'white',
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={client.transactions.length}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Fade>

            {/* Package Analytics Section */}
            {analytics.packageAnalytics && analytics.packageAnalytics.length > 0 && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                        <Fade in={true}>
                            <Paper sx={{ p: 3, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Inventory sx={{ mr: 1, color: '#1976d2' }} />
                                    <Typography variant="h6">
                                        Package Analytics (in {selectedCurrency})
                                    </Typography>
                                </Box>
                                <Grid container spacing={2}>
                                    {analytics.packageAnalytics.map((pkg, index) => (
                                        <Grid item xs={12} sm={6} md={4} key={pkg.name}>
                                            <Card sx={{
                                                borderLeft: `4px solid ${COLORS[index % COLORS.length]}`,
                                                bgcolor: '#f8f9fa'
                                            }}>
                                                <CardContent>
                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                        {pkg.name}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Count: {pkg.count}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Total: {formatCurrency(pkg.total_amount)}
                                                        </Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                        </Fade>
                    </Grid>
                </Grid>
            )}

            {/* Currency Breakdown */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                    <Fade in={true}>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Revenue by Original Currency (Converted to {selectedCurrency})
                            </Typography>
                            <Grid container spacing={2}>
                                {Object.entries(analytics.currencyBreakdown).map(([currency, data]) => (
                                    <Grid item xs={6} sm={4} md={3} key={currency}>
                                        <Card sx={{
                                            bgcolor: currency === 'USD' ? '#e3f2fd' :
                                                currency === 'KES' ? '#e8f5e9' :
                                                    '#f5f5f5',
                                            borderLeft: `4px solid ${currency === 'USD' ? '#1976d2' :
                                                currency === 'KES' ? '#4caf50' :
                                                    '#888'
                                                }`
                                        }}>
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">
                                                    {currency} ({data.count} transactions)
                                                </Typography>
                                                <Typography variant="h6" fontWeight="bold">
                                                    {formatCurrency(data.converted_total)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {analytics.totalRevenue > 0
                                                        ? ((data.converted_total / analytics.totalRevenue) * 100).toFixed(1)
                                                        : 0}% of total
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Original: {formatCurrency(data.total, currency)}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    </Fade>
                </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={8}>
                    <Fade in={true}>
                        <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Revenue vs Profit Trend (in {selectedCurrency})
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={analytics.monthlyTrend}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <RechartsTooltip
                                        formatter={(value) => formatCurrency(value)}
                                    />
                                    <Legend />
                                    <Area
                                        type="monotone"
                                        dataKey="revenue"
                                        stroke="#8884d8"
                                        fill="#8884d8"
                                        fillOpacity={0.3}
                                        name={`Revenue (${selectedCurrency})`}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="profit"
                                        stroke="#82ca9d"
                                        fill="#82ca9d"
                                        fillOpacity={0.3}
                                        name={`Profit (${selectedCurrency})`}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Fade>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Fade in={true}>
                        <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Status Distribution
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={Object.entries(analytics.statusBreakdown).map(([name, value]) => ({
                                            name,
                                            value
                                        }))}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={true}
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={isMobile ? 60 : 80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {Object.keys(analytics.statusBreakdown).map((key, index) => (
                                            <Cell key={key} fill={STATUS_COLORS[key] || COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Fade>
                </Grid>
            </Grid>


            {/* Service Charts */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <Fade in={true}>
                        <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Service/Package Popularity
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={Object.entries(analytics.serviceCount).map(([name, value]) => ({
                                    name,
                                    value
                                }))}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        angle={-45}
                                        textAnchor="end"
                                        interval={0}
                                        height={80}
                                        tick={{ fontSize: 10 }}
                                    />
                                    <YAxis />
                                    <RechartsTooltip />
                                    <Legend />
                                    <Bar dataKey="value" fill="#8884d8" name="Bookings">
                                        {Object.keys(analytics.serviceCount).map((_, index) => (
                                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Fade>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Fade in={true}>
                        <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Revenue by Service/Package (in {selectedCurrency})
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                    layout="vertical"
                                    data={analytics.serviceRevenue.slice(0, 10)}
                                    margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" />
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        width={80}
                                        tick={{ fontSize: 10 }}
                                    />
                                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                                    <Legend />
                                    <Bar dataKey="value" fill="#82ca9d" name={`Revenue (${selectedCurrency})`} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Fade>
                </Grid>
            </Grid>
        </Container>
    );
}